/**
 * Creation of a translation (rows are edited inline in the list). A new
 * translation is created for every language of the configuration.
 */
import { useState, type SubmitEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Dialog } from '@/shared/ui/Dialog';
import { createTranslation } from './api';

export interface TranslationDialogProps {
  languages: readonly string[];
  sectionPrefix: string;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

interface Values {
  section: string;
  key: string;
  value: string;
}

export function TranslationDialog({
  languages,
  sectionPrefix,
  onClose,
}: Readonly<TranslationDialogProps>) {
  const [values, setValues] = useState<Values>({
    section: '',
    key: '',
    value: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [dirty, setDirty] = useState<Set<keyof Values>>(() => new Set());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const set = (key: keyof Values, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty((prev) => new Set(prev).add(key));
  };
  const missing = (key: keyof Values) => !values[key];
  const invalidClass = (key: keyof Values) =>
    dirty.has(key) && missing(key) ? 'invalid' : '';

  const save = useMutation({
    mutationFn: () =>
      Promise.all(
        languages.map((language) =>
          createTranslation({
            ...values,
            section: `${sectionPrefix}${values.section}`,
            language,
          }),
        ),
      ),
    onSuccess: () => onClose('Confirm'),
    onError: () => setErrorMessage('Failed to create translation'),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    if (missing('section') || missing('key') || missing('value')) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  return (
    <Dialog
      title="Translation creation"
      onClose={() => onClose('Cancel')}
      width="500px"
      testId="translation-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className="mb-2">
          <label className="field-label" htmlFor="translation-section">
            Section
          </label>
          {sectionPrefix ? (
            <div className="input-group mt-1">
              <span className="input-group-text" aria-hidden="true">
                {sectionPrefix}
              </span>
              <input
                id="translation-section"
                data-testid="translation-form-section"
                className={`field form-control mt-0 ${invalidClass('section')}`}
                name="section"
                placeholder="Section"
                value={values.section}
                onChange={(event) => set('section', event.target.value)}
              />
            </div>
          ) : (
            <input
              id="translation-section"
              data-testid="translation-form-section"
              className={`field ${invalidClass('section')}`}
              name="section"
              placeholder="Section"
              value={values.section}
              onChange={(event) => set('section', event.target.value)}
            />
          )}
          <small
            hidden={!(submitted && missing('section'))}
            data-testid="translation-form-section-error"
          >
            Wrong section
          </small>
        </div>
        <div className="mb-2">
          <label className="field-label" htmlFor="translation-key">
            Key
          </label>
          <textarea
            id="translation-key"
            data-testid="translation-form-key"
            className={`field ${invalidClass('key')}`}
            name="key"
            placeholder="Key"
            value={values.key}
            onChange={(event) => set('key', event.target.value)}
          />
          <small
            hidden={!(submitted && missing('key'))}
            data-testid="translation-form-key-error"
          >
            Wrong key
          </small>
        </div>
        <div className="mb-2">
          <label className="field-label" htmlFor="translation-value">
            Value
          </label>
          <input
            id="translation-value"
            data-testid="translation-form-value"
            className={`field ${invalidClass('value')}`}
            name="value"
            placeholder="Value"
            value={values.value}
            onChange={(event) => set('value', event.target.value)}
          />
          <small
            hidden={!(submitted && missing('value'))}
            data-testid="translation-form-value-error"
          >
            Wrong value
          </small>
        </div>
        {errorMessage && (
          <div
            className="alert mb-0"
            role="alert"
            data-testid="translation-form-error"
          >
            <span className="error-msg">{errorMessage}</span>
          </div>
        )}
        <div className="mt-6 flex justify-center">
          <button
            className="button button-form"
            type="submit"
            disabled={save.isPending}
            data-testid="translation-form-save-button"
          >
            Save
          </button>
        </div>
      </form>
    </Dialog>
  );
}
